import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../core/api/api";
import useLiveSync from "../hooks/useLiveSync";

const NAV = [
  { to: "/",              icon: "📊", label: "Dashboard",     desc: "Overview & stats"   },
  { to: "/products",      icon: "📦", label: "Products",      desc: "Manage listings"    },
  { to: "/sell-existing", icon: "🏬", label: "Sell Existing",  desc: "Add offer to catalog" },
  { to: "/orders",        icon: "🛒", label: "Orders",        desc: "Track & fulfill"    },
  { to: "/notifications", icon: "🔔", label: "Notifications", desc: "Updates from admin" },
  { to: "/earnings",      icon: "💰", label: "Earnings",      desc: "Revenue & payouts"  },
  { to: "/profile",       icon: "⚙️",  label: "Profile",      desc: "Shop settings"      },
];

export default function Sidebar({ open = false, onClose }) {
  const { seller, logout } = useAuth();
  const initial = seller?.shopName?.[0]?.toUpperCase() || "S";
  const isApproved = seller?.status === "approved";
  const location = useLocation();

  const [unread, setUnread] = useState(0);
  const loadUnread = useCallback(() => api.get("/seller/notifications/unread-count")
    .then(r => setUnread(r.data?.count || 0))
    .catch(() => {}), []);
  // re-check on navigate (e.g. leaving the notifications page)
  useEffect(() => { loadUnread(); }, [location.pathname, loadUnread]);
  // single-source smart polling (pauses when tab hidden, refreshes on focus)
  useLiveSync(loadUnread, { intervalMs: 60000 });

  return (
    <aside className={`seller-sidebar${open ? " open" : ""}`} style={{
      width: 240,
      background: "linear-gradient(180deg, #0f172a 0%, #0d1526 100%)",
      display: "flex", flexDirection: "column",
      position: "fixed", top: 0, left: 0, zIndex: 50,
      borderRight: "1px solid rgba(255,255,255,0.06)"
    }}>

      {/* ── LOGO ── */}
      <div style={{ padding: "22px 20px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg,#fbbf24,#f59e0b)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#0f172a", flexShrink: 0
          }}>V</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", letterSpacing: -0.3 }}>VARAii</div>
            <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em" }}>SELLER PANEL</div>
          </div>
        </div>
      </div>

      {/* ── SELLER PROFILE ── */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 800, color: "#fff",
            boxShadow: "0 4px 12px rgba(99,102,241,0.4)"
          }}>
            {initial}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {seller?.shopName || "My Shop"}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>
              {seller?.name || ""}
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5,
              padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
              background: isApproved ? "rgba(34,197,94,0.12)" : "rgba(234,179,8,0.12)",
              color: isApproved ? "#4ade80" : "#fbbf24",
              border: `1px solid ${isApproved ? "rgba(34,197,94,0.2)" : "rgba(234,179,8,0.2)"}`
            }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: isApproved ? "#22c55e" : "#eab308", display: "inline-block" }} />
              {isApproved ? "ACTIVE" : "PENDING"}
            </div>
          </div>
        </div>
        {seller?.commission != null && (
          <div style={{ marginTop: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#64748b" }}>My Commission</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#fbbf24" }}>{seller.commission}%</span>
          </div>
        )}
      </div>

      {/* ── NAV ── */}
      <nav style={{ flex: 1, padding: "14px 12px", overflowY: "auto" }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#334155", letterSpacing: "0.1em", padding: "0 8px", marginBottom: 8 }}>NAVIGATION</div>
        {NAV.map(({ to, icon, label, desc }) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={() => onClose?.()} style={({ isActive }) => ({
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 12px", borderRadius: 12, marginBottom: 3,
            textDecoration: "none", transition: "all 0.15s",
            background: isActive ? "rgba(37,99,235,0.18)" : "transparent",
            border: isActive ? "1px solid rgba(37,99,235,0.2)" : "1px solid transparent",
          })}>
            {({ isActive }) => (
              <>
                <div style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
                  background: isActive ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.05)",
                  transition: "all 0.15s"
                }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#93c5fd" : "#94a3b8", lineHeight: 1, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 10, color: isActive ? "#60a5fa" : "#475569" }}>{desc}</div>
                </div>
                {to === "/notifications" && unread > 0 && (
                  <span style={{ marginLeft: "auto", background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, minWidth: 18, height: 18, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* ── BOTTOM ── */}
      <div style={{ padding: "12px 12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        {/* Visit store */}
        <a href="https://varaii.com" target="_blank" rel="noopener noreferrer" style={{
          display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10,
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)",
          textDecoration: "none", marginBottom: 8, transition: "background 0.15s"
        }}>
          <span style={{ fontSize: 14 }}>🌐</span>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Visit varaii.com</span>
        </a>

        {/* Logout */}
        <button onClick={logout} style={{
          width: "100%", background: "rgba(239,68,68,0.08)", color: "#f87171",
          border: "1px solid rgba(239,68,68,0.15)", borderRadius: 10,
          padding: "10px 12px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 10, transition: "background 0.15s"
        }}
          onMouseEnter={e => e.currentTarget.style.background="rgba(239,68,68,0.14)"}
          onMouseLeave={e => e.currentTarget.style.background="rgba(239,68,68,0.08)"}
        >
          <span>🚪</span> Sign Out
        </button>
      </div>
    </aside>
  );
}
